import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../redux/slices/authSlice';
import ForgotPasswordModal from './ForgotPasswordModal';
import ForgotEmailModal from './ForgotEmailModal';

const Login = ({ onLoginSuccess, onCancel, entity = 'RMS', redirectPath = '/' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginUserId, setLoginUserId] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showForgotEmail, setShowForgotEmail] = useState(false);

  // AES encryption constants
  const AES_IV = 'ao2wlsaskes5vpws';
  const AES_KEY = 'lsql93slksklasdf2psdfjqlkdf4vpws';
  const KEY_SEPARATOR = ':#:#:';

  // Convert string to Uint8Array
  const stringToUint8Array = (str) => {
    const encoder = new TextEncoder();
    return encoder.encode(str);
  };

  // Convert Base64 string to Uint8Array
  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  // Convert Uint8Array to Unicode string (UTF-16LE)
  const uint8ArrayToUnicodeString = (arr) => {
    let result = '';
    for (let i = 0; i < arr.length; i += 2) {
      result += String.fromCharCode(arr[i] | (arr[i + 1] << 8));
    }
    return result;
  };

  // Encrypt password (matching C# implementation)
  const encryptPassword = async (plainPassword) => {
    try {
      const iv = stringToUint8Array(AES_IV);
      const key = stringToUint8Array(AES_KEY);

      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        key,
        { name: 'AES-CBC' },
        false,
        ['encrypt']
      );

      const encoder = new TextEncoder();
      const dataToEncrypt = encoder.encode(plainPassword);

      const encryptedBuffer = await crypto.subtle.encrypt(
        { name: 'AES-CBC', iv: iv },
        cryptoKey,
        dataToEncrypt
      );

      const encryptedArray = new Uint8Array(encryptedBuffer);
      let binaryString = '';
      for (let i = 0; i < encryptedArray.length; i++) {
        binaryString += String.fromCharCode(encryptedArray[i]);
      }
      
      return btoa(binaryString);
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt password');
    }
  };

  // Decrypt AES encrypted data (matching C# implementation)
  const decryptPassword = async (encryptedData) => {
    try {
      const iv = stringToUint8Array(AES_IV);
      const key = stringToUint8Array(AES_KEY);

      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        key,
        { name: 'AES-CBC' },
        false,
        ['decrypt']
      );

      const encryptedBytes = base64ToUint8Array(encryptedData);

      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-CBC', iv: iv },
        cryptoKey,
        encryptedBytes
      );

      const decryptedData = uint8ArrayToUnicodeString(new Uint8Array(decryptedBuffer));
      const parts = decryptedData.split(KEY_SEPARATOR);

      if (parts.length >= 2) {
        return parts[0];
      }

      return decryptedData;
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt password');
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      if (!loginUserId.trim() || !loginPassword.trim()) {
        setAuthError('Please enter both User ID and Password');
        setAuthLoading(false);
        return;
      }

      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/ykrestapi';
      
      // Use ValidateUserDirect endpoint with plain text password
      const endpoint = `${apiBaseUrl}/CommonChannel/ValidateUserDirect`;

      console.log('Login attempt:', { Email: loginUserId, Password: '***' });
      console.log('API endpoint:', endpoint);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Email: loginUserId,
          Password: loginPassword
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      // Handle 204 No Content - credentials not found or invalid
      if (response.status === 204) {
        console.log('204 No Content - Invalid credentials or user not found');
        setAuthError('Invalid User ID or Password');
        return;
      }

      // Handle 401 Unauthorized - invalid credentials
      if (response.status === 401) {
        console.log('401 Unauthorized - Invalid credentials');
        setAuthError('Invalid User ID or Password');
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Login response data:', data);

      if (!data || (!data.user_Id && !data.userId)) {
        setAuthError('User ID not found');
        return;
      }

      // Login successful - user data returned from API
      setAuthError('');
      // Dispatch to Redux store
      dispatch(login({ email: loginUserId, entity, userData: data }));
      // Call parent callback if provided
      if (onLoginSuccess && typeof onLoginSuccess === 'function') {
        onLoginSuccess({ email: loginUserId, entity, userData: data });
      } else {
        // No callback provided, navigate to redirectPath
        navigate(redirectPath);
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(`Authentication failed: ${error.message || 'Please check your credentials.'}`);
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
      {/* Minimalist Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Subtle geometric pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.05) 35px, rgba(0,0,0,.05) 70px)`
        }}></div>
      </div>

      {/* Clean White Card */}
      <div className="relative bg-white rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.08)] max-w-md w-full p-10 animate-slideInUp border border-gray-100">
        {/* Minimal top accent */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-black to-transparent"></div>

        {/* Clean Header */}
        <div className="text-center mb-10 relative">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-2xl shadow-xl mb-5 transform hover:scale-105 transition-all duration-300">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
            Welcome Back
          </h2>
          <p className="text-gray-500 text-sm">Sign in to continue</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          {/* Email Input - Minimalist */}
          <div className="relative">
            <input
              type="text"
              value={loginUserId}
              onChange={(e) => setLoginUserId(e.target.value)}
              className="w-full px-4 py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-gray-100 outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400"
              placeholder="Email / User ID"
              disabled={authLoading}
              id="email"
            />
          </div>

          {/* Password Input - Minimalist */}
          <div className="relative">
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              className="w-full px-4 py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-gray-100 outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400"
              placeholder="Password"
              disabled={authLoading}
              id="password"
            />
          </div>

          {/* Error Message with Animation */}
          {authError && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 animate-shake">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 text-sm font-medium">{authError}</p>
              </div>
            </div>
          )}

          {/* Buttons - Minimalist Side by Side */}
          <div className="flex gap-3 pt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={authLoading}
              className="flex-1 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-4 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={authLoading}
              className="flex-1 bg-black text-white font-bold py-4 px-4 rounded-xl hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {authLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative py-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500 font-medium">New here?</span>
            </div>
          </div>

          {/* Guest Login Button */}
          <button
            type="button"
            onClick={() => {
              onCancel();
              navigate('/guest-login', { state: { redirectPath } });
            }}
            className="w-full py-4 px-6 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all duration-200 mb-3"
          >
            Continue as Guest
          </button>

          {/* Register and Forgot Password - Minimalist */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => {
                onCancel();
                navigate('/register', { state: { redirectPath } });
              }}
              className="w-full py-4 px-6 bg-white border-2 border-gray-200 text-gray-900 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
            >
              Create New Account
            </button>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="flex-1 py-3 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 bg-blue-50 rounded-lg hover:bg-blue-100"
              >
                Forgot Password?
              </button>
              <button
                type="button"
                onClick={() => setShowForgotEmail(true)}
                className="flex-1 py-3 text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors duration-200 bg-purple-50 rounded-lg hover:bg-purple-100"
              >
                Forgot Email?
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Modals */}
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
      {showForgotEmail && (
        <ForgotEmailModal onClose={() => setShowForgotEmail(false)} />
      )}
    </div>
  );
};

export default Login;
